"use client";

import clsx from "clsx";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * ISO 20022 message ribbon — a quiet footer strip on gate-cascade + LC
 * screens that shows which MX-native message family is in flight, with the
 * legacy MT equivalent on hover.
 *
 *   tsmt.019 → tsrv.001 → tsmt.014 → tsmt.013 → pacs.008
 *
 * The "active" message highlights via the Finance pillar token; predecessors
 * sit at half-opacity; the trailing one waits.
 *
 * Pulled from OrbitraOS Messaging Matrix (sheet "1. Msg Matrix").
 */
export type IsoMessage = {
  mx: string;        // MX native ISO 20022 ID
  mt: string;        // MT legacy equivalent
  family: string;    // TSMT / TSRV / TSIN / pacs
  label: string;     // Short human label
};

const DEFAULT_PIPELINE: IsoMessage[] = [
  { mx: "tsmt.019", mt: "MT798",  family: "TSMT",  label: "Initial baseline submission" },
  { mx: "tsrv.001", mt: "MT700",  family: "TSRV",  label: "Undertaking issuance (LC)" },
  { mx: "tsmt.014", mt: "MT7xx",  family: "TSMT",  label: "Data-set submission" },
  { mx: "tsmt.013", mt: "MT7xx",  family: "TSMT",  label: "Match report" },
  { mx: "pacs.008", mt: "MT103",  family: "pacs",  label: "Customer credit transfer" },
];

export function Iso20022Ribbon({
  pipeline = DEFAULT_PIPELINE,
  activeIndex = -1,
  className,
}: {
  pipeline?: IsoMessage[];
  /** Index of the currently-in-flight message. -1 = none yet. */
  activeIndex?: number;
  className?: string;
}) {
  const t = useTranslations("iso20022Ribbon");
  return (
    <div className={clsx("glass flex items-center gap-3 px-3 py-2", className)}>
      <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed shrink-0">
        {t("kicker")}
      </span>
      <div className="flex items-center gap-1 overflow-x-auto">
        {pipeline.map((m, i) => {
          const before = i < activeIndex;
          const active = i === activeIndex;
          return (
            <span key={m.mx} className="flex items-center gap-1">
              <span
                title={`${m.label} · MT legacy ${m.mt} · family ${m.family}`}
                className={clsx(
                  "rounded-md border px-2 py-0.5 font-mono text-[10px]",
                  active
                    ? "border-state-financed/60 bg-state-financed/15 text-state-financed shadow-[0_0_12px_var(--state-financed-glow)]"
                    : before
                      ? "border-state-financed/30 bg-state-financed/5 text-state-financed/80"
                      : "border-orbitra-muted/30 bg-orbitra-ink/40 text-orbitra-muted",
                )}
              >
                {m.mx}
              </span>
              {i < pipeline.length - 1 && (
                <span className={clsx(
                  "font-mono text-[9px]",
                  i < activeIndex ? "text-state-financed/70" : "text-orbitra-muted/60",
                )}>
                  →
                </span>
              )}
            </span>
          );
        })}
      </div>
      <span className="ms-auto font-mono text-[9px] text-orbitra-muted shrink-0">
        {t("legacyHint")}
      </span>
    </div>
  );
}
